/* eslint-disable @typescript-eslint/no-explicit-any */
import { Directive, DoCheck, inject, Input, OnDestroy, signal } from '@angular/core';
import { ControlValueAccessor, NG_VALIDATORS, NgControl, NgModel, RequiredValidator, Validators } from '@angular/forms';
import { ControlRootComponent } from './control-root.component';
import { Subscription } from 'rxjs';
import { tap } from 'rxjs/operators';
import { coerceBooleanProperty } from '@angular/cdk/coercion';
import { StrakitValidators } from '@ngx-strakit/validation/validator';

@Directive()
export abstract class BaseControlAccessorComponent extends ControlRootComponent implements OnDestroy, DoCheck, ControlValueAccessor {
  @Input() label: string;
  @Input() hint: string;
  @Input() hintRight: string;

  required = signal(false);

  propagateChangeCore: (_: any) => void;
  propagateTouchedCore: () => void;

  protected validators = inject(NG_VALIDATORS, { optional: true }) || [];
  protected ngModelSubscription: Subscription;

  constructor() {
    const ngControl = inject(NgControl);
    super(ngControl);
    if (this.ngControl) {
      this.ngControl.valueAccessor = this;
      if (this.ngControl instanceof NgModel) { this.setupNgModel(); }
    }
  }

  ngDoCheck(): void {
    const hasValidator = this.ngControl.control.hasValidator(Validators.required) || this.ngControl.control.hasValidator(StrakitValidators.noWhitespace);
    const foundValidator = this.validators?.find(v => v.constructor === RequiredValidator) as RequiredValidator;
    const isFoundValidatorRequired = !!foundValidator && coerceBooleanProperty(foundValidator.required);

    this.required.set(hasValidator || isFoundValidatorRequired);
  }

  protected setupNgModel(): void {
    this.ngModelSubscription = this.ngControl.valueChanges.pipe(
      tap(t => {
        if ((this.ngControl.control as unknown as { _pendingChange: boolean })._pendingChange) {
          this.ngControl.viewToModelUpdate(t);
        }
      }),
    ).subscribe();
  }

  protected propagateChange(newValue: any): void {
    if (this.outerControl?.value !== newValue) {
      this.propagateChangeCore?.(newValue);
    }
  }

  protected propagateTouched(): void {
    this.propagateTouchedCore();
  }

  registerOnChange(fn: () => unknown): void {
    this.propagateChangeCore = fn;
  }

  registerOnTouched(fn: () => unknown): void {
    this.propagateTouchedCore = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    if (isDisabled && this.ngControl.enabled) { this.ngControl.control.disable(); }
    if (!isDisabled && this.ngControl.disabled) { this.ngControl.control.enable(); }
  }

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  writeValue(_obj: any): void {

  }

  ngOnDestroy(): void {
    this.ngModelSubscription?.unsubscribe();
  }
}
